/*!
 * devextreme-angular
 * Version: 19.1.3
 * Build date: Tue May 14 2019
 *
 * Copyright (c) 2012 - 2019 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */











import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxTreeList from 'devextreme/ui/tree_list';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxIntegrationModule } from '../core/integration';
import { DxTemplateModule } from '../core/template';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxoColumnChooserModule } from './nested/column-chooser';
import { DxoColumnFixingModule } from './nested/column-fixing';
import { DxoTextsModule } from './nested/texts';
import { DxiColumnModule } from './nested/column-dxi';
import { DxiButtonModule } from './nested/button-dxi';
import { DxoHeaderFilterModule } from './nested/header-filter';
import { DxoLookupModule } from './nested/lookup';
import { DxoFormatModule } from './nested/format';
import { DxoFormItemModule } from './nested/form-item';
import { DxoLabelModule } from './nested/label';
import { DxiValidationRuleModule } from './nested/validation-rule-dxi';
import { DxoEditingModule } from './nested/editing';
import { DxoFormModule } from './nested/form';
import { DxoColCountByScreenModule } from './nested/col-count-by-screen';
import { DxiItemModule } from './nested/item-dxi';
import { DxoTabPanelOptionsModule } from './nested/tab-panel-options';
import { DxiTabModule } from './nested/tab-dxi';
import { DxoButtonOptionsModule } from './nested/button-options';
import { DxoPopupModule } from './nested/popup';
import { DxoAnimationModule } from './nested/animation';
import { DxoHideModule } from './nested/hide';
import { DxoShowModule } from './nested/show';
import { DxoPositionModule } from './nested/position';
import { DxoAtModule } from './nested/at';
import { DxoBoundaryOffsetModule } from './nested/boundary-offset';
import { DxoCollisionModule } from './nested/collision';
import { DxoMyModule } from './nested/my';
import { DxoOffsetModule } from './nested/offset';
import { DxiToolbarItemModule } from './nested/toolbar-item-dxi';
import { DxoFilterBuilderModule } from './nested/filter-builder';
import { DxiCustomOperationModule } from './nested/custom-operation-dxi';
import { DxiFieldModule } from './nested/field-dxi';
import { DxoFilterOperationDescriptionsModule } from './nested/filter-operation-descriptions';
import { DxoGroupOperationDescriptionsModule } from './nested/group-operation-descriptions';
import { DxoFilterBuilderPopupModule } from './nested/filter-builder-popup';
import { DxoFilterPanelModule } from './nested/filter-panel';
import { DxoFilterRowModule } from './nested/filter-row';
import { DxoOperationDescriptionsModule } from './nested/operation-descriptions';
import { DxoKeyboardNavigationModule } from './nested/keyboard-navigation';
import { DxoLoadPanelModule } from './nested/load-panel';
import { DxoPagerModule } from './nested/pager';
import { DxoPagingModule } from './nested/paging';
import { DxoRemoteOperationsModule } from './nested/remote-operations';
import { DxoScrollingModule } from './nested/scrolling';
import { DxoSearchPanelModule } from './nested/search-panel';
import { DxoSelectionModule } from './nested/selection';
import { DxoSortingModule } from './nested/sorting';
import { DxoStateStoringModule } from './nested/state-storing';

import { DxiColumnComponent } from './nested/column-dxi';




/**
 * The TreeList is a widget that represents data from a local or remote source in the form of a multi-column tree view. This widget offers such features as sorting, filtering, editing, selection, etc.
 */
@Component({
    selector: 'dx-tree-list',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxTreeListComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxTreeList;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    @Input()
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the widget changes its state when interacting with a user.
     */
    @Input()
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies whether a user can reorder columns.
     */
    @Input()
    get allowColumnReordering(): boolean {
        return this._getOption('allowColumnReordering');
    }
    set allowColumnReordering(value: boolean) {
        this._setOption('allowColumnReordering', value);
    }


    /**
     * Specifies whether a user can resize columns.
     */
    @Input()
    get allowColumnResizing(): boolean {
        return this._getOption('allowColumnResizing');
    }
    set allowColumnResizing(value: boolean) {
        this._setOption('allowColumnResizing', value);
    }


    /**
     * Specifies whether all rows are expanded initially.
     */
    @Input()
    get autoExpandAll(): boolean {
        return this._getOption('autoExpandAll');
    }
    set autoExpandAll(value: boolean) {
        this._setOption('autoExpandAll', value);
    }


    /**
     * Specifies whether data should be cached.
     */
    @Input()
    get cacheEnabled(): boolean {
        return this._getOption('cacheEnabled');
    }
    set cacheEnabled(value: boolean) {
        this._setOption('cacheEnabled', value);
    }


    /**
     * Enables a hint that appears when a user hovers the mouse pointer over a cell with truncated content.
     */
    @Input()
    get cellHintEnabled(): boolean {
        return this._getOption('cellHintEnabled');
    }
    set cellHintEnabled(value: boolean) {
        this._setOption('cellHintEnabled', value);
    }


    /**
     * Specifies whether columns should adjust their widths to the content.
     */
    @Input()
    get columnAutoWidth(): boolean {
        return this._getOption('columnAutoWidth');
    }
    set columnAutoWidth(value: boolean) {
        this._setOption('columnAutoWidth', value);
    }


    /**
     * Configures the column chooser.
     */
    @Input()
    get columnChooser(): { allowSearch?: boolean, emptyPanelText?: string, enabled?: boolean, height?: number, mode?: string, searchTimeout?: number, title?: string, width?: number } {
        return this._getOption('columnChooser');
    }
    set columnChooser(value: { allowSearch?: boolean, emptyPanelText?: string, enabled?: boolean, height?: number, mode?: string, searchTimeout?: number, title?: string, width?: number }) {
        this._setOption('columnChooser', value);
    }


    /**
     * Configures column fixing.
     */
    @Input()
    get columnFixing(): { enabled?: boolean, texts?: { fix?: string, leftPosition?: string, rightPosition?: string, unfix?: string } } {
        return this._getOption('columnFixing');
    }
    set columnFixing(value: { enabled?: boolean, texts?: { fix?: string, leftPosition?: string, rightPosition?: string, unfix?: string } }) {
        this._setOption('columnFixing', value);
    }


    /**
     * Specifies whether the widget should hide columns to adapt to the screen or container size. Ignored if allowColumnResizing is true and columnResizingMode is "widget".
     */
    @Input()
    get columnHidingEnabled(): boolean {
        return this._getOption('columnHidingEnabled');
    }
    set columnHidingEnabled(value: boolean) {
        this._setOption('columnHidingEnabled', value);
    }


    /**
     * Specifies the minimum width of columns.
     */
    @Input()
    get columnMinWidth(): number {
        return this._getOption('columnMinWidth');
    }
    set columnMinWidth(value: number) {
        this._setOption('columnMinWidth', value);
    }


    /**
     * Specifies how the widget resizes columns. Applies only if allowColumnResizing is true.
     */
    @Input()
    get columnResizingMode(): string {
        return this._getOption('columnResizingMode');
    }
    set columnResizingMode(value: string) {
        this._setOption('columnResizingMode', value);
    }


    /**
     * Configures columns.
     */
    @Input()
    get columns(): Array<DevExpress.ui.dxTreeListColumn | string> {
        return this._getOption('columns');
    }
    set columns(value: Array<DevExpress.ui.dxTreeListColumn | string>) {
        this._setOption('columns', value);
    }


    /**
     * Specifies the width for all data columns. Has a lower priority than the column.width option.
     */
    @Input()
    get columnWidth(): number {
        return this._getOption('columnWidth');
    }
    set columnWidth(value: number) {
        this._setOption('columnWidth', value);
    }


    /**
     * Customizes columns after they are created.
     */
    @Input()
    get customizeColumns(): Function {
        return this._getOption('customizeColumns');
    }
    set customizeColumns(value: Function) {
        this._setOption('customizeColumns', value);
    }


    /**
     * Binds the widget to data.
     */
    @Input()
    get dataSource(): DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<any> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<any>) {
        this._setOption('dataSource', value);
    }


    /**
     * Notifies the widget of the used data structure.
     */
    @Input()
    get dataStructure(): string {
        return this._getOption('dataStructure');
    }
    set dataStructure(value: string) {
        this._setOption('dataStructure', value);
    }


    /**
     * Specifies date-time values' serialization format. Use it only if you do not specify the dataSource at design time.
     */
    @Input()
    get dateSerializationFormat(): string {
        return this._getOption('dateSerializationFormat');
    }
    set dateSerializationFormat(value: string) {
        this._setOption('dateSerializationFormat', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Configures editing.
     */
    @Input()
    get editing(): { allowAdding?: boolean | Function, allowDeleting?: boolean | Function, allowUpdating?: boolean | Function, form?: DevExpress.ui.dxFormOptions, mode?: string, popup?: DevExpress.ui.dxPopupOptions, refreshMode?: string, selectTextOnEditStart?: boolean, startEditAction?: string, texts?: { addRow?: string, addRowToNode?: string, cancelAllChanges?: string, cancelRowChanges?: string, confirmDeleteMessage?: string, confirmDeleteTitle?: string, deleteRow?: string, editRow?: string, saveAllChanges?: string, saveRowChanges?: string, undeleteRow?: string, validationCancelChanges?: string }, useIcons?: boolean } {
        return this._getOption('editing');
    }
    set editing(value: { allowAdding?: boolean | Function, allowDeleting?: boolean | Function, allowUpdating?: boolean | Function, form?: DevExpress.ui.dxFormOptions, mode?: string, popup?: DevExpress.ui.dxPopupOptions, refreshMode?: string, selectTextOnEditStart?: boolean, startEditAction?: string, texts?: { addRow?: string, addRowToNode?: string, cancelAllChanges?: string, cancelRowChanges?: string, confirmDeleteMessage?: string, confirmDeleteTitle?: string, deleteRow?: string, editRow?: string, saveAllChanges?: string, saveRowChanges?: string, undeleteRow?: string, validationCancelChanges?: string }, useIcons?: boolean }) {
        this._setOption('editing', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Indicates whether to show the error row.
     */
    @Input()
    get errorRowEnabled(): boolean {
        return this._getOption('errorRowEnabled');
    }
    set errorRowEnabled(value: boolean) {
        this._setOption('errorRowEnabled', value);
    }


    /**
     * Specifies keys of the initially expanded rows.
     */
    @Input()
    get expandedRowKeys(): Array<any> {
        return this._getOption('expandedRowKeys');
    }
    set expandedRowKeys(value: Array<any>) {
        this._setOption('expandedRowKeys', value);
    }


    /**
     * Specifies whether nodes appear expanded or collapsed after filtering is applied.
     */
    @Input()
    get expandNodesOnFiltering(): boolean {
        return this._getOption('expandNodesOnFiltering');
    }
    set expandNodesOnFiltering(value: boolean) {
        this._setOption('expandNodesOnFiltering', value);
    }


    /**
     * Configures the integrated filter builder.
     */
    @Input()
    get filterBuilder(): DevExpress.ui.dxFilterBuilderOptions {
        return this._getOption('filterBuilder');
    }
    set filterBuilder(value: DevExpress.ui.dxFilterBuilderOptions) {
        this._setOption('filterBuilder', value);
    }


    /**
     * Configures the popup in which the integrated filter builder is shown.
     */
    @Input()
    get filterBuilderPopup(): DevExpress.ui.dxPopupOptions {
        return this._getOption('filterBuilderPopup');
    }
    set filterBuilderPopup(value: DevExpress.ui.dxPopupOptions) {
        this._setOption('filterBuilderPopup', value);
    }


    /**
     * Specifies whether to show filtered rows with ancestors and descendants (full branch) or with ancestors only.
     */
    @Input()
    get filterMode(): string {
        return this._getOption('filterMode');
    }
    set filterMode(value: string) {
        this._setOption('filterMode', value);
    }


    /**
     * Configures the filter panel.
     */
    @Input()
    get filterPanel(): { customizeText?: Function, filterEnabled?: boolean, texts?: { clearFilter?: string, createFilter?: string, filterEnabledHint?: string }, visible?: boolean } {
        return this._getOption('filterPanel');
    }
    set filterPanel(value: { customizeText?: Function, filterEnabled?: boolean, texts?: { clearFilter?: string, createFilter?: string, filterEnabledHint?: string }, visible?: boolean }) {
        this._setOption('filterPanel', value);
    }


    /**
     * Configures the filter row.
     */
    @Input()
    get filterRow(): { applyFilter?: string, applyFilterText?: string, betweenEndText?: string, betweenStartText?: string, operationDescriptions?: { between?: string, contains?: string, endsWith?: string, equal?: string, greaterThan?: string, greaterThanOrEqual?: string, lessThan?: string, lessThanOrEqual?: string, notContains?: string, notEqual?: string, startsWith?: string }, resetOperationText?: string, showAllText?: string, showOperationChooser?: boolean, visible?: boolean } {
        return this._getOption('filterRow');
    }
    set filterRow(value: { applyFilter?: string, applyFilterText?: string, betweenEndText?: string, betweenStartText?: string, operationDescriptions?: { between?: string, contains?: string, endsWith?: string, equal?: string, greaterThan?: string, greaterThanOrEqual?: string, lessThan?: string, lessThanOrEqual?: string, notContains?: string, notEqual?: string, startsWith?: string }, resetOperationText?: string, showAllText?: string, showOperationChooser?: boolean, visible?: boolean }) {
        this._setOption('filterRow', value);
    }


    /**
     * Specifies whether to synchronize the filter row, header filter, and filter builder. The synchronized filter expression is stored in the filterValue option.
     */
    @Input()
    get filterSyncEnabled(): boolean {
        return this._getOption('filterSyncEnabled');
    }
    set filterSyncEnabled(value: boolean) {
        this._setOption('filterSyncEnabled', value);
    }


    /**
     * Specifies a filter expression.
     */
    @Input()
    get filterValue(): any {
        return this._getOption('filterValue');
    }
    set filterValue(value: any) {
        this._setOption('filterValue', value);
    }


    /**
     * Specifies the index of the column focused initially or currently in the data row area.
     */
    @Input()
    get focusedColumnIndex(): number {
        return this._getOption('focusedColumnIndex');
    }
    set focusedColumnIndex(value: number) {
        this._setOption('focusedColumnIndex', value);
    }


    /**
     * Specifies whether the focused row feature is enabled.
     */
    @Input()
    get focusedRowEnabled(): boolean {
        return this._getOption('focusedRowEnabled');
    }
    set focusedRowEnabled(value: boolean) {
        this._setOption('focusedRowEnabled', value);
    }


    /**
     * Specifies the initially or currently focused grid row's index. Use it when focusedRowEnabled is true.
     */
    @Input()
    get focusedRowIndex(): number {
        return this._getOption('focusedRowIndex');
    }
    set focusedRowIndex(value: number) {
        this._setOption('focusedRowIndex', value);
    }


    /**
     * Specifies initially or currently focused grid row's key. Use it when focusedRowEnabled is true.
     */
    @Input()
    get focusedRowKey(): any {
        return this._getOption('focusedRowKey');
    }
    set focusedRowKey(value: any) {
        this._setOption('focusedRowKey', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies which data field defines whether the node has children.
     */
    @Input()
    get hasItemsExpr(): Function | string {
        return this._getOption('hasItemsExpr');
    }
    set hasItemsExpr(value: Function | string) {
        this._setOption('hasItemsExpr', value);
    }


    /**
     * Configures the header filter feature.
     */
    @Input()
    get headerFilter(): { allowSearch?: boolean, height?: number, searchTimeout?: number, texts?: { cancel?: string, emptyValue?: string, ok?: string }, visible?: boolean, width?: number } {
        return this._getOption('headerFilter');
    }
    set headerFilter(value: { allowSearch?: boolean, height?: number, searchTimeout?: number, texts?: { cancel?: string, emptyValue?: string, ok?: string }, visible?: boolean, width?: number }) {
        this._setOption('headerFilter', value);
    }


    /**
     * Specifies the widget's height.
     */
    @Input()
    get height(): number | Function | string {
        return this._getOption('height');
    }
    set height(value: number | Function | string) {
        this._setOption('height', value);
    }


    /**
     * Specifies whether to highlight rows and cells whose data changed.
     */
    @Input()
    get highlightChanges(): boolean {
        return this._getOption('highlightChanges');
    }
    set highlightChanges(value: boolean) {
        this._setOption('highlightChanges', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    @Input()
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies which data field contains nested items. Set this option when your data has a hierarchical structure.
     */
    @Input()
    get itemsExpr(): Function | string {
        return this._getOption('itemsExpr');
    }
    set itemsExpr(value: Function | string) {
        this._setOption('itemsExpr', value);
    }


    /**
     * Configures keyboard navigation.
     */
    @Input()
    get keyboardNavigation(): { editOnKeyPress?: boolean, enterKeyAction?: string, enterKeyDirection?: string } {
        return this._getOption('keyboardNavigation');
    }
    set keyboardNavigation(value: { editOnKeyPress?: boolean, enterKeyAction?: string, enterKeyDirection?: string }) {
        this._setOption('keyboardNavigation', value);
    }


    /**
     * Specifies which data field provides keys for nodes.
     */
    @Input()
    get keyExpr(): Function | string {
        return this._getOption('keyExpr');
    }
    set keyExpr(value: Function | string) {
        this._setOption('keyExpr', value);
    }


    /**
     * Configures the load panel.
     */
    @Input()
    get loadPanel(): { enabled?: boolean, height?: number, indicatorSrc?: string, shading?: boolean, shadingColor?: string, showIndicator?: boolean, showPane?: boolean, text?: string, width?: number } {
        return this._getOption('loadPanel');
    }
    set loadPanel(value: { enabled?: boolean, height?: number, indicatorSrc?: string, shading?: boolean, shadingColor?: string, showIndicator?: boolean, showPane?: boolean, text?: string, width?: number }) {
        this._setOption('loadPanel', value);
    }


    /**
     * Specifies text shown when the widget does not display any data.
     */
    @Input()
    get noDataText(): string {
        return this._getOption('noDataText');
    }
    set noDataText(value: string) {
        this._setOption('noDataText', value);
    }


    /**
     * Configures the pager.
     */
    @Input()
    get pager(): { allowedPageSizes?: Array<number>, infoText?: string, showInfo?: boolean, showNavigationButtons?: boolean, showPageSizeSelector?: boolean, visible?: boolean } {
        return this._getOption('pager');
    }
    set pager(value: { allowedPageSizes?: Array<number>, infoText?: string, showInfo?: boolean, showNavigationButtons?: boolean, showPageSizeSelector?: boolean, visible?: boolean }) {
        this._setOption('pager', value);
    }


    /**
     * Configures paging.
     */
    @Input()
    get paging(): { enabled?: boolean, pageIndex?: number, pageSize?: number } {
        return this._getOption('paging');
    }
    set paging(value: { enabled?: boolean, pageIndex?: number, pageSize?: number }) {
        this._setOption('paging', value);
    }


    /**
     * Specifies which data field provides parent keys.
     */
    @Input()
    get parentIdExpr(): Function | string {
        return this._getOption('parentIdExpr');
    }
    set parentIdExpr(value: Function | string) {
        this._setOption('parentIdExpr', value);
    }


    /**
     * Notifies the TreeList of the server's data processing operations. Applies only if data has a plain structure.
     */
    @Input()
    get remoteOperations(): { filtering?: boolean, grouping?: boolean, sorting?: boolean } {
        return this._getOption('remoteOperations');
    }
    set remoteOperations(value: { filtering?: boolean, grouping?: boolean, sorting?: boolean }) {
        this._setOption('remoteOperations', value);
    }


    /**
     * Specifies whether to render the filter row, command columns, and columns with showEditorAlways set to true after other elements.
     */
    @Input()
    get renderAsync(): boolean {
        return this._getOption('renderAsync');
    }
    set renderAsync(value: boolean) {
        this._setOption('renderAsync', value);
    }


    /**
     * Specifies whether to repaint only those cells whose data changed.
     */
    @Input()
    get repaintChangesOnly(): boolean {
        return this._getOption('repaintChangesOnly');
    }
    set repaintChangesOnly(value: boolean) {
        this._setOption('repaintChangesOnly', value);
    }


    /**
     * Specifies the root node's identifier. Applies if dataStructure is "plain".
     */
    @Input()
    get rootValue(): any {
        return this._getOption('rootValue');
    }
    set rootValue(value: any) {
        this._setOption('rootValue', value);
    }


    /**
     * Specifies whether rows should be shaded differently.
     */
    @Input()
    get rowAlternationEnabled(): boolean {
        return this._getOption('rowAlternationEnabled');
    }
    set rowAlternationEnabled(value: boolean) {
        this._setOption('rowAlternationEnabled', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Configures scrolling.
     */
    @Input()
    get scrolling(): { columnRenderingMode?: string, mode?: string, preloadEnabled?: boolean, rowRenderingMode?: string, scrollByContent?: boolean, scrollByThumb?: boolean, showScrollbar?: string, useNative?: boolean } {
        return this._getOption('scrolling');
    }
    set scrolling(value: { columnRenderingMode?: string, mode?: string, preloadEnabled?: boolean, rowRenderingMode?: string, scrollByContent?: boolean, scrollByThumb?: boolean, showScrollbar?: string, useNative?: boolean }) {
        this._setOption('scrolling', value);
    }


    /**
     * Configures the search panel.
     */
    @Input()
    get searchPanel(): { highlightCaseSensitive?: boolean, highlightSearchText?: boolean, placeholder?: string, searchVisibleColumnsOnly?: boolean, text?: string, visible?: boolean, width?: number } {
        return this._getOption('searchPanel');
    }
    set searchPanel(value: { highlightCaseSensitive?: boolean, highlightSearchText?: boolean, placeholder?: string, searchVisibleColumnsOnly?: boolean, text?: string, visible?: boolean, width?: number }) {
        this._setOption('searchPanel', value);
    }


    /**
     * Allows you to select rows or determine which rows are selected.
     */
    @Input()
    get selectedRowKeys(): Array<any> {
        return this._getOption('selectedRowKeys');
    }
    set selectedRowKeys(value: Array<any>) {
        this._setOption('selectedRowKeys', value);
    }


    /**
     * Configures runtime selection.
     */
    @Input()
    get selection(): { allowSelectAll?: boolean, mode?: string, recursive?: boolean } {
        return this._getOption('selection');
    }
    set selection(value: { allowSelectAll?: boolean, mode?: string, recursive?: boolean }) {
        this._setOption('selection', value);
    }


    /**
     * Specifies whether the outer borders of the widget are visible.
     */
    @Input()
    get showBorders(): boolean {
        return this._getOption('showBorders');
    }
    set showBorders(value: boolean) {
        this._setOption('showBorders', value);
    }


    /**
     * Specifies whether column headers are visible.
     */
    @Input()
    get showColumnHeaders(): boolean {
        return this._getOption('showColumnHeaders');
    }
    set showColumnHeaders(value: boolean) {
        this._setOption('showColumnHeaders', value);
    }


    /**
     * Specifies whether vertical lines that separate one column from another are visible.
     */
    @Input()
    get showColumnLines(): boolean {
        return this._getOption('showColumnLines');
    }
    set showColumnLines(value: boolean) {
        this._setOption('showColumnLines', value);
    }


    /**
     * Specifies whether horizontal lines that separate one row from another are visible.
     */
    @Input()
    get showRowLines(): boolean {
        return this._getOption('showRowLines');
    }
    set showRowLines(value: boolean) {
        this._setOption('showRowLines', value);
    }


    /**
     * Configures runtime sorting.
     */
    @Input()
    get sorting(): { ascendingText?: string, clearText?: string, descendingText?: string, mode?: string } {
        return this._getOption('sorting');
    }
    set sorting(value: { ascendingText?: string, clearText?: string, descendingText?: string, mode?: string }) {
        this._setOption('sorting', value);
    }


    /**
     * Configures state storing.
     */
    @Input()
    get stateStoring(): { customLoad?: Function, customSave?: Function, enabled?: boolean, savingTimeout?: number, storageKey?: string, type?: string } {
        return this._getOption('stateStoring');
    }
    set stateStoring(value: { customLoad?: Function, customSave?: Function, enabled?: boolean, savingTimeout?: number, storageKey?: string, type?: string }) {
        this._setOption('stateStoring', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies whether to enable two-way data binding.
     */
    @Input()
    get twoWayBindingEnabled(): boolean {
        return this._getOption('twoWayBindingEnabled');
    }
    set twoWayBindingEnabled(value: boolean) {
        this._setOption('twoWayBindingEnabled', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    @Input()
    get width(): number | Function | string {
        return this._getOption('width');
    }
    set width(value: number | Function | string) {
        this._setOption('width', value);
    }


    /**
     * Specifies whether text that does not fit into a column should be wrapped.
     */
    @Input()
    get wordWrapEnabled(): boolean {
        return this._getOption('wordWrapEnabled');
    }
    set wordWrapEnabled(value: boolean) {
        this._setOption('wordWrapEnabled', value);
    }

    /**
     * A function that is executed before an adaptive detail row is rendered.
     */
    @Output() onAdaptiveDetailRowPreparing: EventEmitter<any>;

    /**
     * A function that is executed when a cell is clicked or tapped. Executed before onRowClick.
     */
    @Output() onCellClick: EventEmitter<any>;

    /**
     * A function that is executed when a cell is double-clicked or double-tapped. Executed before onRowDblClick.
     */
    @Output() onCellDblClick: EventEmitter<any>;

    /**
     * A function that is executed after the pointer enters or leaves a cell.
     */
    @Output() onCellHoverChanged: EventEmitter<any>;

    /**
     * A function that is executed after a grid cell is created.
     */
    @Output() onCellPrepared: EventEmitter<any>;

    /**
     * A function that is executed when the widget's content is ready and each time the content is changed.
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
     * A function that is executed before the context menu is rendered.
     */
    @Output() onContextMenuPreparing: EventEmitter<any>;

    /**
     * A function that is executed when an error occurs in the data source.
     */
    @Output() onDataErrorOccurred: EventEmitter<any>;

    /**
     * A function that is executed before the widget is disposed of.
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
     * A function that is executed before a cell or row switches to the editing state.
     */
    @Output() onEditingStart: EventEmitter<any>;

    /**
     * A function that is executed after an editor is created.
     */
    @Output() onEditorPrepared: EventEmitter<any>;

    /**
     * A function that is executed before a cell's editor is created. Not executed for cells with an editCellTemplate.
     */
    @Output() onEditorPreparing: EventEmitter<any>;

    /**
     * A function that is executed after the focused cell changes.
     */
    @Output() onFocusedCellChanged: EventEmitter<any>;

    /**
     * A function that is executed before the focused cell changes.
     */
    @Output() onFocusedCellChanging: EventEmitter<any>;

    /**
     * A function that executed when the focused row changes. Applies only when focusedRowEnabled is true.
     */
    @Output() onFocusedRowChanged: EventEmitter<any>;

    /**
     * A function that is executed before the focused row changes. Applies only when focusedRowEnabled is true.
     */
    @Output() onFocusedRowChanging: EventEmitter<any>;

    /**
     * A function that is executed only once, after the widget is initialized.
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
     * A function that is executed before a new row is added to the widget.
     */
    @Output() onInitNewRow: EventEmitter<any>;

    /**
     * A function that is executed when the widget is in focus and a key has been pressed down.
     */
    @Output() onKeyDown: EventEmitter<any>;

    /**
     * A function that is executed after the loaded nodes are initialized.
     */
    @Output() onNodesInitialized: EventEmitter<any>;

    /**
     * A function that is executed after a widget option is changed.
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
     * A function that is executed when a grid row is clicked or tapped.
     */
    @Output() onRowClick: EventEmitter<any>;

    /**
     * A function that is executed after a row is collapsed.
     */
    @Output() onRowCollapsed: EventEmitter<any>;

    /**
     * A function that is executed before a row is collapsed.
     */
    @Output() onRowCollapsing: EventEmitter<any>;

    /**
     * A function that is executed when a row is double-clicked or double-tapped. Executed after onCellDblClick.
     */
    @Output() onRowDblClick: EventEmitter<any>;

    /**
     * A function that is executed after a row is expanded.
     */
    @Output() onRowExpanded: EventEmitter<any>;

    /**
     * A function that is executed before a row is expanded.
     */
    @Output() onRowExpanding: EventEmitter<any>;

    /**
     * A function that is executed after a new row has been inserted into the data source.
     */
    @Output() onRowInserted: EventEmitter<any>;

    /**
     * A function that is executed before a new row is inserted into the data source.
     */
    @Output() onRowInserting: EventEmitter<any>;

    /**
     * A function that is executed after a row is created.
     */
    @Output() onRowPrepared: EventEmitter<any>;

    /**
     * A function that is executed after a row has been removed from the data source.
     */
    @Output() onRowRemoved: EventEmitter<any>;

    /**
     * A function that is executed before a row is removed from the data source.
     */
    @Output() onRowRemoving: EventEmitter<any>;

    /**
     * A function that is executed after a row has been updated in the data source.
     */
    @Output() onRowUpdated: EventEmitter<any>;

    /**
     * A function that is executed before a row is updated in the data source.
     */
    @Output() onRowUpdating: EventEmitter<any>;

    /**
     * A function that is executed after cells in a row are validated against validation rules.
     */
    @Output() onRowValidating: EventEmitter<any>;

    /**
     * A function that is executed after selecting a row or clearing its selection.
     */
    @Output() onSelectionChanged: EventEmitter<any>;

    /**
     * A function that is executed before the toolbar is created.
     */
    @Output() onToolbarPreparing: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
    @Output() accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
    @Output() activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the allowColumnReorderingChange event.
     */
    @Output() allowColumnReorderingChange: EventEmitter<boolean>;

    /**
     * A handler for the allowColumnResizingChange event.
     */
    @Output() allowColumnResizingChange: EventEmitter<boolean>;

    /**
     * A handler for the autoExpandAllChange event.
     */
    @Output() autoExpandAllChange: EventEmitter<boolean>;

    /**
     * A handler for the cacheEnabledChange event.
     */
    @Output() cacheEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the cellHintEnabledChange event.
     */
    @Output() cellHintEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the columnAutoWidthChange event.
     */
    @Output() columnAutoWidthChange: EventEmitter<boolean>;

    /**
     * A handler for the columnChooserChange event.
     */
    @Output() columnChooserChange: EventEmitter<{ allowSearch?: boolean, emptyPanelText?: string, enabled?: boolean, height?: number, mode?: string, searchTimeout?: number, title?: string, width?: number }>;

    /**
     * A handler for the columnFixingChange event.
     */
    @Output() columnFixingChange: EventEmitter<{ enabled?: boolean, texts?: { fix?: string, leftPosition?: string, rightPosition?: string, unfix?: string } }>;

    /**
     * A handler for the columnHidingEnabledChange event.
     */
    @Output() columnHidingEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the columnMinWidthChange event.
     */
    @Output() columnMinWidthChange: EventEmitter<number>;

    /**
     * A handler for the columnResizingModeChange event.
     */
    @Output() columnResizingModeChange: EventEmitter<string>;

    /**
     * A handler for the columnsChange event.
     */
    @Output() columnsChange: EventEmitter<Array<DevExpress.ui.dxTreeListColumn | string>>;

    /**
     * A handler for the columnWidthChange event.
     */
    @Output() columnWidthChange: EventEmitter<number>;

    /**
     * A handler for the customizeColumnsChange event.
     */
    @Output() customizeColumnsChange: EventEmitter<Function>;

    /**
     * A handler for the dataSourceChange event.
     */
    @Output() dataSourceChange: EventEmitter<DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<any>>;

    /**
     * A handler for the dataStructureChange event.
     */
    @Output() dataStructureChange: EventEmitter<string>;

    /**
     * A handler for the dateSerializationFormatChange event.
     */
    @Output() dateSerializationFormatChange: EventEmitter<string>;

    /**
     * A handler for the disabledChange event.
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the editingChange event.
     */
    @Output() editingChange: EventEmitter<{ allowAdding?: boolean | Function, allowDeleting?: boolean | Function, allowUpdating?: boolean | Function, form?: DevExpress.ui.dxFormOptions, mode?: string, popup?: DevExpress.ui.dxPopupOptions, refreshMode?: string, selectTextOnEditStart?: boolean, startEditAction?: string, texts?: { addRow?: string, addRowToNode?: string, cancelAllChanges?: string, cancelRowChanges?: string, confirmDeleteMessage?: string, confirmDeleteTitle?: string, deleteRow?: string, editRow?: string, saveAllChanges?: string, saveRowChanges?: string, undeleteRow?: string, validationCancelChanges?: string }, useIcons?: boolean }>;

    /**
     * A handler for the elementAttrChange event.
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the errorRowEnabledChange event.
     */
    @Output() errorRowEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the expandedRowKeysChange event.
     */
    @Output() expandedRowKeysChange: EventEmitter<Array<any>>;

    /**
     * A handler for the expandNodesOnFilteringChange event.
     */
    @Output() expandNodesOnFilteringChange: EventEmitter<boolean>;

    /**
     * A handler for the filterBuilderChange event.
     */
    @Output() filterBuilderChange: EventEmitter<DevExpress.ui.dxFilterBuilderOptions>;

    /**
     * A handler for the filterBuilderPopupChange event.
     */
    @Output() filterBuilderPopupChange: EventEmitter<DevExpress.ui.dxPopupOptions>;

    /**
     * A handler for the filterModeChange event.
     */
    @Output() filterModeChange: EventEmitter<string>;

    /**
     * A handler for the filterPanelChange event.
     */
    @Output() filterPanelChange: EventEmitter<{ customizeText?: Function, filterEnabled?: boolean, texts?: { clearFilter?: string, createFilter?: string, filterEnabledHint?: string }, visible?: boolean }>;

    /**
     * A handler for the filterRowChange event.
     */
    @Output() filterRowChange: EventEmitter<{ applyFilter?: string, applyFilterText?: string, betweenEndText?: string, betweenStartText?: string, operationDescriptions?: { between?: string, contains?: string, endsWith?: string, equal?: string, greaterThan?: string, greaterThanOrEqual?: string, lessThan?: string, lessThanOrEqual?: string, notContains?: string, notEqual?: string, startsWith?: string }, resetOperationText?: string, showAllText?: string, showOperationChooser?: boolean, visible?: boolean }>;

    /**
     * A handler for the filterSyncEnabledChange event.
     */
    @Output() filterSyncEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the filterValueChange event.
     */
    @Output() filterValueChange: EventEmitter<any>;

    /**
     * A handler for the focusedColumnIndexChange event.
     */
    @Output() focusedColumnIndexChange: EventEmitter<number>;

    /**
     * A handler for the focusedRowEnabledChange event.
     */
    @Output() focusedRowEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the focusedRowIndexChange event.
     */
    @Output() focusedRowIndexChange: EventEmitter<number>;

    /**
     * A handler for the focusedRowKeyChange event.
     */
    @Output() focusedRowKeyChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the hasItemsExprChange event.
     */
    @Output() hasItemsExprChange: EventEmitter<Function | string>;

    /**
     * A handler for the headerFilterChange event.
     */
    @Output() headerFilterChange: EventEmitter<{ allowSearch?: boolean, height?: number, searchTimeout?: number, texts?: { cancel?: string, emptyValue?: string, ok?: string }, visible?: boolean, width?: number }>;

    /**
     * A handler for the heightChange event.
     */
    @Output() heightChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the highlightChangesChange event.
     */
    @Output() highlightChangesChange: EventEmitter<boolean>;

    /**
     * A handler for the hintChange event.
     */
    @Output() hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the itemsExprChange event.
     */
    @Output() itemsExprChange: EventEmitter<Function | string>;

    /**
     * A handler for the keyboardNavigationChange event.
     */
    @Output() keyboardNavigationChange: EventEmitter<{ editOnKeyPress?: boolean, enterKeyAction?: string, enterKeyDirection?: string }>;

    /**
     * A handler for the keyExprChange event.
     */
    @Output() keyExprChange: EventEmitter<Function | string>;

    /**
     * A handler for the loadPanelChange event.
     */
    @Output() loadPanelChange: EventEmitter<{ enabled?: boolean, height?: number, indicatorSrc?: string, shading?: boolean, shadingColor?: string, showIndicator?: boolean, showPane?: boolean, text?: string, width?: number }>;

    /**
     * A handler for the noDataTextChange event.
     */
    @Output() noDataTextChange: EventEmitter<string>;

    /**
     * A handler for the pagerChange event.
     */
    @Output() pagerChange: EventEmitter<{ allowedPageSizes?: Array<number>, infoText?: string, showInfo?: boolean, showNavigationButtons?: boolean, showPageSizeSelector?: boolean, visible?: boolean }>;

    /**
     * A handler for the pagingChange event.
     */
    @Output() pagingChange: EventEmitter<{ enabled?: boolean, pageIndex?: number, pageSize?: number }>;

    /**
     * A handler for the parentIdExprChange event.
     */
    @Output() parentIdExprChange: EventEmitter<Function | string>;

    /**
     * A handler for the remoteOperationsChange event.
     */
    @Output() remoteOperationsChange: EventEmitter<{ filtering?: boolean, grouping?: boolean, sorting?: boolean }>;

    /**
     * A handler for the renderAsyncChange event.
     */
    @Output() renderAsyncChange: EventEmitter<boolean>;

    /**
     * A handler for the repaintChangesOnlyChange event.
     */
    @Output() repaintChangesOnlyChange: EventEmitter<boolean>;

    /**
     * A handler for the rootValueChange event.
     */
    @Output() rootValueChange: EventEmitter<any>;

    /**
     * A handler for the rowAlternationEnabledChange event.
     */
    @Output() rowAlternationEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the rtlEnabledChange event.
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the scrollingChange event.
     */
    @Output() scrollingChange: EventEmitter<{ columnRenderingMode?: string, mode?: string, preloadEnabled?: boolean, rowRenderingMode?: string, scrollByContent?: boolean, scrollByThumb?: boolean, showScrollbar?: string, useNative?: boolean }>;

    /**
     * A handler for the searchPanelChange event.
     */
    @Output() searchPanelChange: EventEmitter<{ highlightCaseSensitive?: boolean, highlightSearchText?: boolean, placeholder?: string, searchVisibleColumnsOnly?: boolean, text?: string, visible?: boolean, width?: number }>;

    /**
     * A handler for the selectedRowKeysChange event.
     */
    @Output() selectedRowKeysChange: EventEmitter<Array<any>>;

    /**
     * A handler for the selectionChange event.
     */
    @Output() selectionChange: EventEmitter<{ allowSelectAll?: boolean, mode?: string, recursive?: boolean }>;

    /**
     * A handler for the showBordersChange event.
     */
    @Output() showBordersChange: EventEmitter<boolean>;

    /**
     * A handler for the showColumnHeadersChange event.
     */
    @Output() showColumnHeadersChange: EventEmitter<boolean>;

    /**
     * A handler for the showColumnLinesChange event.
     */
    @Output() showColumnLinesChange: EventEmitter<boolean>;

    /**
     * A handler for the showRowLinesChange event.
     */
    @Output() showRowLinesChange: EventEmitter<boolean>;

    /**
     * A handler for the sortingChange event.
     */
    @Output() sortingChange: EventEmitter<{ ascendingText?: string, clearText?: string, descendingText?: string, mode?: string }>;

    /**
     * A handler for the stateStoringChange event.
     */
    @Output() stateStoringChange: EventEmitter<{ customLoad?: Function, customSave?: Function, enabled?: boolean, savingTimeout?: number, storageKey?: string, type?: string }>;

    /**
     * A handler for the tabIndexChange event.
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the twoWayBindingEnabledChange event.
     */
    @Output() twoWayBindingEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the visibleChange event.
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
    @Output() widthChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the wordWrapEnabledChange event.
     */
    @Output() wordWrapEnabledChange: EventEmitter<boolean>;




    @ContentChildren(DxiColumnComponent)
    get columnsChildren(): QueryList<DxiColumnComponent> {
        return this._getOption('columns');
    }
    set columnsChildren(value) {
        this.setChildren('columns', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'adaptiveDetailRowPreparing', emit: 'onAdaptiveDetailRowPreparing' },
            { subscribe: 'cellClick', emit: 'onCellClick' },
            { subscribe: 'cellDblClick', emit: 'onCellDblClick' },
            { subscribe: 'cellHoverChanged', emit: 'onCellHoverChanged' },
            { subscribe: 'cellPrepared', emit: 'onCellPrepared' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'contextMenuPreparing', emit: 'onContextMenuPreparing' },
            { subscribe: 'dataErrorOccurred', emit: 'onDataErrorOccurred' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'editingStart', emit: 'onEditingStart' },
            { subscribe: 'editorPrepared', emit: 'onEditorPrepared' },
            { subscribe: 'editorPreparing', emit: 'onEditorPreparing' },
            { subscribe: 'focusedCellChanged', emit: 'onFocusedCellChanged' },
            { subscribe: 'focusedCellChanging', emit: 'onFocusedCellChanging' },
            { subscribe: 'focusedRowChanged', emit: 'onFocusedRowChanged' },
            { subscribe: 'focusedRowChanging', emit: 'onFocusedRowChanging' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'initNewRow', emit: 'onInitNewRow' },
            { subscribe: 'keyDown', emit: 'onKeyDown' },
            { subscribe: 'nodesInitialized', emit: 'onNodesInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'rowClick', emit: 'onRowClick' },
            { subscribe: 'rowCollapsed', emit: 'onRowCollapsed' },
            { subscribe: 'rowCollapsing', emit: 'onRowCollapsing' },
            { subscribe: 'rowDblClick', emit: 'onRowDblClick' },
            { subscribe: 'rowExpanded', emit: 'onRowExpanded' },
            { subscribe: 'rowExpanding', emit: 'onRowExpanding' },
            { subscribe: 'rowInserted', emit: 'onRowInserted' },
            { subscribe: 'rowInserting', emit: 'onRowInserting' },
            { subscribe: 'rowPrepared', emit: 'onRowPrepared' },
            { subscribe: 'rowRemoved', emit: 'onRowRemoved' },
            { subscribe: 'rowRemoving', emit: 'onRowRemoving' },
            { subscribe: 'rowUpdated', emit: 'onRowUpdated' },
            { subscribe: 'rowUpdating', emit: 'onRowUpdating' },
            { subscribe: 'rowValidating', emit: 'onRowValidating' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'toolbarPreparing', emit: 'onToolbarPreparing' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'allowColumnReorderingChange' },
            { emit: 'allowColumnResizingChange' },
            { emit: 'autoExpandAllChange' },
            { emit: 'cacheEnabledChange' },
            { emit: 'cellHintEnabledChange' },
            { emit: 'columnAutoWidthChange' },
            { emit: 'columnChooserChange' },
            { emit: 'columnFixingChange' },
            { emit: 'columnHidingEnabledChange' },
            { emit: 'columnMinWidthChange' },
            { emit: 'columnResizingModeChange' },
            { emit: 'columnsChange' },
            { emit: 'columnWidthChange' },
            { emit: 'customizeColumnsChange' },
            { emit: 'dataSourceChange' },
            { emit: 'dataStructureChange' },
            { emit: 'dateSerializationFormatChange' },
            { emit: 'disabledChange' },
            { emit: 'editingChange' },
            { emit: 'elementAttrChange' },
            { emit: 'errorRowEnabledChange' },
            { emit: 'expandedRowKeysChange' },
            { emit: 'expandNodesOnFilteringChange' },
            { emit: 'filterBuilderChange' },
            { emit: 'filterBuilderPopupChange' },
            { emit: 'filterModeChange' },
            { emit: 'filterPanelChange' },
            { emit: 'filterRowChange' },
            { emit: 'filterSyncEnabledChange' },
            { emit: 'filterValueChange' },
            { emit: 'focusedColumnIndexChange' },
            { emit: 'focusedRowEnabledChange' },
            { emit: 'focusedRowIndexChange' },
            { emit: 'focusedRowKeyChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'hasItemsExprChange' },
            { emit: 'headerFilterChange' },
            { emit: 'heightChange' },
            { emit: 'highlightChangesChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemsExprChange' },
            { emit: 'keyboardNavigationChange' },
            { emit: 'keyExprChange' },
            { emit: 'loadPanelChange' },
            { emit: 'noDataTextChange' },
            { emit: 'pagerChange' },
            { emit: 'pagingChange' },
            { emit: 'parentIdExprChange' },
            { emit: 'remoteOperationsChange' },
            { emit: 'renderAsyncChange' },
            { emit: 'repaintChangesOnlyChange' },
            { emit: 'rootValueChange' },
            { emit: 'rowAlternationEnabledChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollingChange' },
            { emit: 'searchPanelChange' },
            { emit: 'selectedRowKeysChange' },
            { emit: 'selectionChange' },
            { emit: 'showBordersChange' },
            { emit: 'showColumnHeadersChange' },
            { emit: 'showColumnLinesChange' },
            { emit: 'showRowLinesChange' },
            { emit: 'sortingChange' },
            { emit: 'stateStoringChange' },
            { emit: 'tabIndexChange' },
            { emit: 'twoWayBindingEnabledChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'wordWrapEnabledChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxTreeList(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('columns', changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('expandedRowKeys', changes);
        this.setupChanges('selectedRowKeys', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('columns');
        this._idh.doCheck('dataSource');
        this._idh.doCheck('expandedRowKeys');
        this._idh.doCheck('selectedRowKeys');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

}

@NgModule({
  imports: [
    DxoColumnChooserModule,
    DxoColumnFixingModule,
    DxoTextsModule,
    DxiColumnModule,
    DxiButtonModule,
    DxoHeaderFilterModule,
    DxoLookupModule,
    DxoFormatModule,
    DxoFormItemModule,
    DxoLabelModule,
    DxiValidationRuleModule,
    DxoEditingModule,
    DxoFormModule,
    DxoColCountByScreenModule,
    DxiItemModule,
    DxoTabPanelOptionsModule,
    DxiTabModule,
    DxoButtonOptionsModule,
    DxoPopupModule,
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxiToolbarItemModule,
    DxoFilterBuilderModule,
    DxiCustomOperationModule,
    DxiFieldModule,
    DxoFilterOperationDescriptionsModule,
    DxoGroupOperationDescriptionsModule,
    DxoFilterBuilderPopupModule,
    DxoFilterPanelModule,
    DxoFilterRowModule,
    DxoOperationDescriptionsModule,
    DxoKeyboardNavigationModule,
    DxoLoadPanelModule,
    DxoPagerModule,
    DxoPagingModule,
    DxoRemoteOperationsModule,
    DxoScrollingModule,
    DxoSearchPanelModule,
    DxoSelectionModule,
    DxoSortingModule,
    DxoStateStoringModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxTreeListComponent
  ],
  exports: [
    DxTreeListComponent,
    DxoColumnChooserModule,
    DxoColumnFixingModule,
    DxoTextsModule,
    DxiColumnModule,
    DxiButtonModule,
    DxoHeaderFilterModule,
    DxoLookupModule,
    DxoFormatModule,
    DxoFormItemModule,
    DxoLabelModule,
    DxiValidationRuleModule,
    DxoEditingModule,
    DxoFormModule,
    DxoColCountByScreenModule,
    DxiItemModule,
    DxoTabPanelOptionsModule,
    DxiTabModule,
    DxoButtonOptionsModule,
    DxoPopupModule,
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxiToolbarItemModule,
    DxoFilterBuilderModule,
    DxiCustomOperationModule,
    DxiFieldModule,
    DxoFilterOperationDescriptionsModule,
    DxoGroupOperationDescriptionsModule,
    DxoFilterBuilderPopupModule,
    DxoFilterPanelModule,
    DxoFilterRowModule,
    DxoOperationDescriptionsModule,
    DxoKeyboardNavigationModule,
    DxoLoadPanelModule,
    DxoPagerModule,
    DxoPagingModule,
    DxoRemoteOperationsModule,
    DxoScrollingModule,
    DxoSearchPanelModule,
    DxoSelectionModule,
    DxoSortingModule,
    DxoStateStoringModule,
    DxTemplateModule
  ]
})
export class DxTreeListModule { }
